import React, { useState, useEffect, useRef, useCallback } from 'react';

interface AnimatedGifProps {
  gifUrls: string[];
  className: string;
  alt: string;
  instanceId: string;
}

// Global state to track used GIFs across all instances
const usedGifs = new Set<string>();
const gifInstances = new Map<string, { getCurrentGif: () => string; setCurrentGif: (gif: string) => void }>();

const AnimatedGif: React.FC<AnimatedGifProps> = ({ gifUrls, className, alt, instanceId }) => {
  const [currentGifIndex, setCurrentGifIndex] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get current GIF URL
  const getCurrentGif = useCallback(() => gifUrls[currentGifIndex], [gifUrls, currentGifIndex]);
  
  // Set current GIF by URL
  const setCurrentGif = useCallback((gifUrl: string) => {
    const index = gifUrls.indexOf(gifUrl);
    if (index !== -1) {
      setCurrentGifIndex(index);
    }
  }, [gifUrls]);

  // Register this instance
  useEffect(() => {
    gifInstances.set(instanceId, { getCurrentGif, setCurrentGif });
    return () => {
      gifInstances.delete(instanceId);
      usedGifs.delete(getCurrentGif());
    };
  }, [instanceId, currentGifIndex, getCurrentGif, setCurrentGif]);

  // Get a unique GIF that's not currently used by any other instance
  const getUniqueGif = useCallback(() => {
    const availableGifs = gifUrls.filter(gif => !usedGifs.has(gif));
    if (availableGifs.length === 0) {
      // If all GIFs are used, clear the set and start fresh
      usedGifs.clear();
      return gifUrls[Math.floor(Math.random() * gifUrls.length)];
    }
    return availableGifs[Math.floor(Math.random() * availableGifs.length)];
  }, [gifUrls]);

  // Initialize with a unique GIF
  useEffect(() => {
    const uniqueGif = getUniqueGif();
    const index = gifUrls.indexOf(uniqueGif);
    if (index !== -1) {
      setCurrentGifIndex(index);
      usedGifs.add(uniqueGif);
    }
  }, [getUniqueGif, gifUrls]);

  useEffect(() => {
    if (gifUrls.length <= 1) return;

    const changeGif = () => {
      // Remove current GIF from used set
      usedGifs.delete(getCurrentGif());
      
      // Get next unique GIF
      const uniqueGif = getUniqueGif();
      const nextIndex = gifUrls.indexOf(uniqueGif);
      if (nextIndex === -1) return;
      
      // Add new GIF to used set and switch immediately
      usedGifs.add(uniqueGif);
      setCurrentGifIndex(nextIndex);
    };

    // Random interval between 6-12 seconds (longer display time)
    const getRandomInterval = () => Math.random() * 6000 + 6000;
    
    const scheduleNextChange = () => {
      timeoutRef.current = setTimeout(() => {
        changeGif();
        scheduleNextChange(); // Schedule the next change
      }, getRandomInterval());
    };

    scheduleNextChange();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [gifUrls, getCurrentGif, getUniqueGif]);

  return (
    <div className={className}>
      <div className="gif-image-container">
        {/* Current GIF */}
        <img 
          src={gifUrls[currentGifIndex]}
          alt={alt}
          className="party-mode-gif"
        />
      </div>
      <div className="party-mode-gif-overlay"></div>
    </div>
  );
};

export default AnimatedGif;
